<template>
  <div
    v-if="appointmentType"
    class="appointment-confirmation"
    :class="[
      {
        'appointment-confirmation': true,
        'appointment-confirmation--test-drive': isTestDrive,
        'appointment-confirmation--adviser': !isTestDrive
      }
    ]"
  >
    <div class="row">
      <div :class="[{ 'col-12 mb-5': isMobile, 'col-5': !isMobile }]">
        <AsyncImage
          :image="image"
          :show-images="showImages"
          :current-image-index-to-load="1"
          @load-next="loadNext()"
          @is-ready="toggleReady(1)"
        />
      </div>
      <div :class="[{ 'col-12': isMobile, 'col-7': !isMobile }]">
        <div class="row g-0 appointment-details">
          <div class="col-12 appointment-details__title mb-3">
            <h3 class="fw-bold">{{ $t('confirm_appointment_page.subtitle') }}</h3>
          </div>
          <div v-if="isTestDrive" class="appointment-details__content mb-3">
            {{ translatedAppointmentType }}
            <span>{{ appointmentItem?.name }}&nbsp;</span>
            <span v-if="selectedVehicles.length > 1" class="appointment-item-additional-vehicles">
              {{ additionalVehicles }}
            </span>
            <br />
            <span class="fw-bold">{{ $t('confirm_appointment_page.date') }}: </span>
            <span>{{ upperFirst(appointmentDate) }}</span>
            <br />
            <span class="fw-bold">{{ $t('confirm_appointment_page.time') }}: </span>
            <span>{{ appointmentTime }}</span>
            <br />
          </div>
          <div v-else class="appointment-details__content mb-3">
            {{ translatedAppointmentType }}
            <span> {{ appointmentItem?.name }} </span><br />

            <span class="fw-bold">{{ $t('confirm_appointment_page.date') }}: </span>
            <span>{{ upperFirst(appointmentDate) }}</span>
            <br />
            <span class="fw-bold">{{ $t('confirm_appointment_page.time') }}: </span>
            <span>{{ appointmentTime }}</span>
            <br />
          </div>
        </div>
      </div>
      <div v-if="isTestDrive" class="col-12 pt-5">
        <div class="card border bg-transparent rounded">
          <div class="card-header fw-bold rounded">
            {{ $t('confirm_appointment_page.disclaimer.important') }}
          </div>
          <div class="card-body bg-transparent fw-light">
            <p>
              {{ $t('confirm_appointment_page.disclaimer.notice') }}
            </p>
            <div
              class="test-drive-requirements"
              v-html="$t('confirm_appointment_page.disclaimer.test_drive_requirements')"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="container-x-padding pt-5">
    <button ref="button" class="o-btn-action" type="button" @click.prevent="goToSubmissionForm()">
      <span>{{ $t('confirm_appointment_page.confirm') }}</span>
    </button>
  </div>
</template>

<script lang="ts">
import { ComputedRef, defineComponent, reactive } from 'vue'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { AppointmentTypeTranslations } from '~conciergeUiCommons/models/Appointment'
import { ConciergeAppointmentQuestionFlow } from '~types/concierge-enum'
import { router, usePage } from '@inertiajs/vue3'
import AsyncImage from '~conciergeUiCommons/components/AsyncImage.vue'
import { AsyncImageData, useShowImage } from '~conciergeUiCommons/composables/showImage'
import { AppointmentItem, Adviser, Vehicle, Appointment } from '~concierge/types/AppointmentTypes'
import AdviserSpokenLanguages from '~conciergeUiCommons/components/AdviserSpokenLanguages.vue'
import { wTransChoice, currentLocale } from 'laravel-vue-i18n'
import { Locale } from 'date-fns'
import { frCA, enCA } from 'date-fns/locale'
import { format as tzFormat } from 'date-fns-tz'
import { useMobileDetection } from '~conciergeUiCommons/composables/deviceDetection'
import { upperFirst } from 'lodash'

export default defineComponent({
  name: 'AppointmentConfirmation',
  components: { AsyncImage, AdviserSpokenLanguages },
  setup() {
    const appointmentStore = useAppointmentStore()
    if (!appointmentStore.isAppointmentSet) {
      const page = usePage()
      const homeUrl = page.props.homeUrl as string
      return router.visit(homeUrl)
    }

    const { isMobile } = useMobileDetection()
    const identityStore = useIdentityStore()
    const { toggleReady, loadNext, showImages } = useShowImage(1)
    const adviserSpokenLanguageEnabled =
      identityStore.identity?.settings?.adviserSpokenLanguageEnabled ?? false
    return {
      isMobile,
      appointmentStore,
      toggleReady,
      loadNext,
      showImages,
      adviserSpokenLanguageEnabled,
      upperFirst
    }
  },
  emits: ['goToSubmissionForm'],
  data() {
    return {
      image: reactive<AsyncImageData>({
        id: 1,
        index: 1,
        url: '',
        width: null,
        height: null,
        alt: '',
        ready: false,
        show: false
      })
    }
  },
  computed: {
    lastName(): string {
      return this.appointment.adviser?.last_name?.replaceAll(/-/g, '\u2011') || ''
    },
    firstName(): string {
      return this.appointment.adviser?.first_name?.replaceAll(/-/g, '\u2011') || ''
    },
    appointment(): Appointment {
      return this.appointmentStore.appointment
    },
    locale(): Locale {
      return currentLocale.value === 'fr' ? frCA : enCA
    },
    appointmentTime(): string {
      const day = this.appointment.date + ' ' + this.appointment.hour
      const format = currentLocale.value === 'fr' ? "HH'h'mm" : 'HH:mm'

      return tzFormat(day, format, {
        locale: this.locale
      })
    },
    appointmentDate(): string {
      const day = this.appointment.date + ' ' + this.appointment.hour
      const format = currentLocale.value === 'fr' ? 'EEEE d MMMM yyyy' : "EEEE',' d MMMM yyyy"

      return tzFormat(day, format, {
        locale: this.locale
      })
    },
    appointmentType(): ConciergeAppointmentQuestionFlow | null {
      return this.appointment.flow
    },
    translatedAppointmentType(): string {
      return this.appointmentType ? AppointmentTypeTranslations[this.appointmentType] : ''
    },
    isTestDrive(): boolean {
      return this.appointmentType === ConciergeAppointmentQuestionFlow.TestDriveAppointment
    },
    appointmentItem(): AppointmentItem {
      return this.appointmentStore.getAppointmentItem
    },
    adviser(): Adviser | null {
      return this.appointment.adviser
    },
    spokenLanguages(): string[] {
      return this.adviser?.spoken_languages ?? []
    },
    selectedVehicles(): Vehicle[] {
      return this.appointmentStore.selectedVehicles
    },
    additionalVehicles(): ComputedRef<string> {
      const count = this.selectedVehicles.length
      if (count === 0) {
        return ''
      }
      return (
        wTransChoice(
          'confirm_appointment_page.additional_vehicles',
          this.selectedVehicles.length - 1
        ) ?? ''
      )
    },
    showAdviserSpokenLanguages(): boolean {
      return !this.isTestDrive && this.adviserSpokenLanguageEnabled
    }
  },
  created() {
    this.image.url = this.appointmentItem?.photo?.url ?? ''
    this.image.width = this.appointmentItem?.photo?.width ?? null
    this.image.height = this.appointmentItem?.photo?.height ?? null
    this.image.alt = this.appointmentItem?.name ?? 'Photo'
  },
  methods: {
    goToSubmissionForm(): void {
      this.appointmentStore.confirmAppointment()
      this.$emit('goToSubmissionForm')
    }
  }
})
</script>
